import React from 'react'
import { resolveMicroTemplateBackground } from './microTemplates'
import MicroMetaFooter from '../../components/micro/MicroMetaFooter'

type Props = {
  text: string
  templateId: string
  categoryLabel?: string | null
  authorName?: string | null
  authorHandle?: string | null
  authorAvatarUrl?: string | null
  authorVerified?: boolean | null
  createdAt?: string | null
  viewsCount?: number | null
  hasProof?: boolean | null
  receiptStatus?: 'pending' | 'confirmed' | 'failed' | string | null
}

type LayoutTier = 'tier-a' | 'tier-b'

function layoutProfile(text: string): {
  tier: LayoutTier
  fontClass: string
  trackingClass: string
  topPct: number
  maxWidthPct: number
  clampMobile: number
  clampDesktop: number
  scrimPct: number
  clampEnabled: boolean
} {
  const raw = String(text || '')
  const t = raw.trim()
  const chars = t.length
  const normalized = raw.replace(/\r\n?/g, '\n').replace(/\n+$/g, '')
  const rows = normalized ? normalized.split('\n') : ['']
  const lines = rows.length
  const blankLines = rows.filter((r) => r.trim().length === 0).length
  // Approximate soft-wraps in the preview content box so prose scales down sooner.
  const estimatedWrappedLines = rows.reduce((acc, row) => {
    const len = row.trim().length
    if (len === 0) return acc + 1
    // 16 chars is a good approximation for large micro typography + max width.
    return acc + Math.max(1, Math.ceil(len / 16))
  }, 0)
  const lineLoad = Math.max(lines, estimatedWrappedLines) + blankLines

  const isVeryLong = chars > 220 || lineLoad > 18
  if (!isVeryLong) {
    return {
      tier: 'tier-a',
      // Big type by default, including medium-length micros.
      fontClass: 'text-[28px] md:text-[32px] lg:text-[36px] leading-[1.18]',
      trackingClass: 'tracking-[0.002em]',
      topPct: 52,
      maxWidthPct: 78,
      clampMobile: 16,
      clampDesktop: 18,
      scrimPct: 55,
      clampEnabled: true,
    }
  }
  const ultraLong = chars > 420 || lines > 10
  const reduceAfter19Lines = lineLoad > 19
  const reduceAfter20Lines = lineLoad > 20
  const reduceAfter21Lines = lineLoad > 21
  const reduceAfter22Lines = lineLoad > 22
  const reduceAfter23Lines = lineLoad > 23
  const reduceAfter25Lines = lineLoad > 25
  const reduceAfter27Lines = lineLoad > 27
  const reduceAfter28Lines = lineLoad > 28
  const reduceAfter29Lines = lineLoad > 29
  return {
    tier: 'tier-b',
    // Stop shrinking after the post-29 step; this is the minimum readable size.
    fontClass: reduceAfter29Lines
      ? 'text-[14px] md:text-[18px] lg:text-[22px] leading-[1.13]'
      : reduceAfter28Lines
        ? 'text-[15px] md:text-[19px] lg:text-[23px] leading-[1.14]'
      : reduceAfter27Lines
        ? 'text-[16px] md:text-[20px] lg:text-[24px] leading-[1.14]'
      : reduceAfter25Lines
        ? 'text-[17px] md:text-[21px] lg:text-[25px] leading-[1.14]'
      : reduceAfter23Lines
        ? 'text-[19px] md:text-[23px] lg:text-[27px] leading-[1.15]'
      : reduceAfter22Lines
        ? 'text-[21px] md:text-[25px] lg:text-[29px] leading-[1.16]'
      : reduceAfter21Lines
        ? 'text-[22px] md:text-[26px] lg:text-[30px] leading-[1.16]'
      : reduceAfter20Lines
        ? 'text-[23px] md:text-[27px] lg:text-[31px] leading-[1.17]'
      : reduceAfter19Lines
        ? 'text-[24px] md:text-[28px] lg:text-[32px] leading-[1.18]'
      : ultraLong
        ? 'text-[26px] md:text-[30px] lg:text-[34px] leading-[1.2]'
        : 'text-[26px] md:text-[30px] lg:text-[34px] leading-[1.2]',
    trackingClass: 'tracking-[0.001em]',
    topPct: 46,
    maxWidthPct: 88,
    // Cap preview at 31 lines.
    clampMobile: 31,
    clampDesktop: 31,
    scrimPct: 65,
    clampEnabled: true,
  }
}

export function computeAdaptiveTop(args: {
  cardH: number
  textH: number
  topSafe: number
  bottomSafe: number
  bias?: number
}): number {
  const cardH = Math.max(0, Number(args.cardH || 0))
  const textH = Math.max(0, Number(args.textH || 0))
  const topSafe = Math.max(0, Number(args.topSafe || 0))
  const bottomSafe = Math.max(0, Number(args.bottomSafe || 0))
  const bias = Math.min(0.7, Math.max(0.2, Number(args.bias ?? 0.45)))
  const avail = Math.max(0, cardH - topSafe - bottomSafe)

  // If the text fits, place it within the safe region with a slight upward editorial bias.
  if (textH <= avail) {
    return Math.max(topSafe, topSafe + (avail - textH) * bias)
  }
  // If it does not fit, anchor to top-safe and rely on clamp+fade (no internal scroll).
  return topSafe
}

function initialsFrom(name: string): string {
  const s = String(name || '').trim()
  if (!s) return '?'
  const parts = s.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function VerifiedBadge() {
  return (
    <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-sky-500/95 text-white shadow-sm" aria-label="Verified">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-3.5 w-3.5">
        <path
          d="M7.9 10.6l-1.7-1.7a1 1 0 10-1.4 1.4l2.4 2.4a1 1 0 001.4 0l6-6a1 1 0 10-1.4-1.4l-5.3 5.3z"
          fill="currentColor"
        />
      </svg>
    </span>
  )
}

export default function MicroPreviewCard({
  text,
  templateId,
  categoryLabel,
  authorName,
  authorHandle,
  authorAvatarUrl,
  authorVerified,
  createdAt,
  viewsCount,
  hasProof,
  receiptStatus,
}: Props) {
  const cardRef = React.useRef<HTMLDivElement | null>(null)
  const textRef = React.useRef<HTMLDivElement | null>(null)
  const watermarkRef = React.useRef<HTMLDivElement | null>(null)
  const authorRef = React.useRef<HTMLDivElement | null>(null)

  const bg = resolveMicroTemplateBackground(templateId)
  const t = String(text || '').trim()
  const profile = layoutProfile(t)
  const categoryUpper = String(categoryLabel || '').trim().toUpperCase()
  const handleRaw = String(authorHandle || '').trim()
  const handleLabel = handleRaw ? (handleRaw.startsWith('@') ? handleRaw : `@${handleRaw}`) : ''
  const nameLabel = String(authorName || '').trim()
  const hasAuthor = Boolean(nameLabel || handleLabel || (authorAvatarUrl && String(authorAvatarUrl).trim()))
  const avatarInitials = initialsFrom(nameLabel || handleRaw || 'Me')
  const [adaptiveTopPx, setAdaptiveTopPx] = React.useState<number>(0)

  React.useLayoutEffect(() => {
    let frame = 0
    const measure = () => {
      const card = cardRef.current
      const text = textRef.current
      if (!card || !text) return

      const cardH = card.clientHeight || 0
      const textH = text.scrollHeight || text.getBoundingClientRect().height || 0
      const watermarkH = watermarkRef.current?.getBoundingClientRect().height || 0
      const authorH = hasAuthor ? (authorRef.current?.getBoundingClientRect().height || 0) : 0

      // Let long text use most of the canvas while still protecting watermark/author.
      const topSafe = profile.tier === 'tier-b'
        ? Math.max(20, watermarkH + 12)
        : Math.max(30, watermarkH + 20)
      const bottomSafe = profile.tier === 'tier-b'
        ? Math.max(24, authorH + 16)
        : Math.max(30, authorH + 24)
      const next = computeAdaptiveTop({
        cardH,
        textH,
        topSafe,
        bottomSafe,
        bias: profile.tier === 'tier-b' ? 0.08 : 0.44,
      })
      setAdaptiveTopPx((prev) => (Math.abs(prev - next) > 0.5 ? next : prev))
    }

    frame = window.requestAnimationFrame(measure)

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null
    if (ro) {
      if (cardRef.current) ro.observe(cardRef.current)
      if (textRef.current) ro.observe(textRef.current)
      if (watermarkRef.current) ro.observe(watermarkRef.current)
      if (authorRef.current) ro.observe(authorRef.current)
    }

    window.addEventListener('resize', measure)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', measure)
      ro?.disconnect()
    }
  }, [t, profile.tier, hasAuthor, categoryUpper, handleLabel, nameLabel, authorAvatarUrl])

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
      <div
        ref={cardRef}
        className="relative w-full overflow-hidden rounded-2xl border border-white/10 shadow-2xl"
        style={{
          background: bg,
          aspectRatio: '9 / 16',
        }}
      >
        {/* Editorial tone + vignette + grain */}
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_10%,rgba(255,255,255,0.06),rgba(0,0,0,0.25))]" />
        <div className="micro-grain absolute inset-0 opacity-[0.12] mix-blend-overlay" />

        {/* Top-left watermark/header (safe-area aware) */}
        <div
          ref={watermarkRef}
          className="absolute z-10"
          style={{
            top: 'calc(16px + env(safe-area-inset-top))',
            left: 'calc(16px + env(safe-area-inset-left))',
          } as any}
        >
          <div className="micro-text-shadow text-[11px] font-semibold tracking-[0.18em] text-white/70">
            PIXE <span className="text-white/55">{'\u2022'}</span> MICRO
          </div>
          {categoryUpper ? (
            <div className="micro-text-shadow mt-1 text-[10px] font-medium tracking-[0.16em] text-white/55">
              {categoryUpper}
            </div>
          ) : null}
        </div>

        {/* Full-width scrim (clean, not a blob) */}
        <div
          className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent"
          style={{ height: `${profile.scrimPct}%` }}
        />

        {/* Text block (auto balanced) */}
        <div
          className="absolute left-4 right-4"
          style={{ top: adaptiveTopPx || `${profile.topPct}%` } as any}
          data-layout-tier={profile.tier}
        >
          <div
            ref={textRef}
            className="text-left"
            style={{
              maxWidth: `${profile.maxWidthPct}%`,
              // Never collide with the author lockup: cap the content region height.
              maxHeight: `calc(100% - ${hasAuthor ? (profile.tier === 'tier-b' ? 0 : 72) : 0}px)`,
            }}
          >
            <div
              className={[
                profile.clampEnabled ? 'micro-clamp' : '',
                'micro-text-shadow',
                'text-white/95',
                'font-medium',
                'whitespace-pre-line',
                profile.fontClass,
                profile.trackingClass,
              ].join(' ')}
              style={
                {
                  ['--micro-lines-mobile' as any]: profile.clampMobile,
                  ['--micro-lines-desktop' as any]: profile.clampDesktop,
                } as any
              }
            >
              {t || 'Your micro text preview will show here...'}
            </div>
          </div>
        </div>

        {/* Author lockup (Micro mode preview identity) */}
        {hasAuthor ? (
          <div
            ref={authorRef}
            className="absolute inset-x-4 flex items-end justify-between"
            style={{ bottom: 'calc(16px + env(safe-area-inset-bottom))' } as any}
          >
            <div className="flex min-w-0 items-center gap-2">
              <div className="h-8 w-8 shrink-0 rounded-full border border-white/15 bg-black/25 overflow-hidden flex items-center justify-center">
                {authorAvatarUrl ? (
                  <img src={authorAvatarUrl} alt={nameLabel || handleLabel || 'Author'} className="h-full w-full object-cover" />
                ) : (
                  <div className="text-[10px] font-semibold text-white/85 tracking-[0.06em]">{avatarInitials}</div>
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <div className="text-[12px] font-semibold text-white/85 truncate">{nameLabel || handleLabel || 'Me'}</div>
                  {authorVerified ? <VerifiedBadge /> : null}
                </div>
                {handleLabel ? <div className="text-[11px] text-white/60 truncate">{handleLabel}</div> : null}
              </div>
            </div>
            <div className="pb-[1px]">
              <MicroMetaFooter
                createdAt={createdAt}
                viewsCount={viewsCount ?? 0}
                hasProof={hasProof}
                receiptStatus={receiptStatus}
                compact
                showViewsLabel
              />
            </div>
          </div>
        ) : (
          <div
            className="absolute inset-x-4 flex justify-end"
            style={{ bottom: 'calc(16px + env(safe-area-inset-bottom))' } as any}
          >
            <MicroMetaFooter
              createdAt={createdAt}
              viewsCount={viewsCount ?? 0}
              hasProof={hasProof}
              receiptStatus={receiptStatus}
              compact
              showViewsLabel
            />
          </div>
        )}
      </div>
    </div>
  )
}
